import Card from "components/Card/Card";
import type { OptionType } from "components/inputs";
import { MaskedInput, Select, TextInput } from "components/inputs";
import SectionBreak from "components/SectionBreak";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { api } from "services/api";
import {
  useGetClientsQuery,
  usePatchClientByIdMutation,
} from "services/client-api";
import {
  useCreateEmployerMutation,
  useGetEmployersQuery,
  usePatchEmployerByIdMutation,
} from "services/employer-api";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";
import type { ClientType, EmployerType } from "types/DataTypes";
import { useToken } from "utils/context";
import { useDispatch, useSelector } from "utils/redux";

const ClientAndEmployerInformation = ({
  isReadOnly = false,
}: { isReadOnly?: boolean } = {}) => {
  const { fileId } = useParams<{ fileId: string }>();
  const constants = useSelector((state) => state.shared.constants);
  const { accessToken } = useToken();
  const dispatch = useDispatch();
  const [patchClientById] = usePatchClientByIdMutation();
  const [patchEmployerById] = usePatchEmployerByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();
  const [createEmployer] = useCreateEmployerMutation();
  const [showEmployerInformation, setShowEmployerInformation] = useState(false);
  const prevSelectedClientIdRef = useRef<number | null>(null);
  const prevSelectedEmployerIdRef = useRef<number | null>(null);

  const [clientOptions, setClientOptions] = useState<OptionType[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState<string | null>(null);
  const [employerOptions, setEmployerOptions] = useState<OptionType[]>([]);
  const [employerSearchTerm, setEmployerSearchTerm] = useState<string | null>(
    null,
  );

  const debouncedSetClientSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setClientSearchTerm(value);
      }, 500),
    [],
  );

  const debouncedSetEmployerSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setEmployerSearchTerm(value);
      }, 500),
    [],
  );

  const { data: clients, isFetching: isClientsFetching } = useGetClientsQuery(
    { searchTerm: clientSearchTerm },
    { skip: !clientSearchTerm },
  );

  useEffect(() => {
    if (clients) {
      setClientOptions(
        clients.results?.map((client: ClientType) => ({
          label: client.name,
          value: client.id,
        })) ?? [],
      );
    }
  }, [clients]);

  const { data: employers, isFetching: isEmployersFetching } =
    useGetEmployersQuery(
      { searchTerm: employerSearchTerm },
      { skip: !employerSearchTerm },
    );

  const {
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm();

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  const clientId = fileData?.client?.id;
  const employerId = fileData?.employer?.id;

  useEffect(() => {
    if (employers) {
      setEmployerOptions(
        employers.results?.map((employer: EmployerType) => ({
          label: employer.name,
          value: employer.id,
        })) ?? [],
      );
    }
  }, [employers]);

  useEffect(() => {
    if (fileData) {
      if (
        !fileData.services?.some((service) => service.type === "111") ||
        (fileData?.claimants && fileData.claimants.length > 0)
      ) {
        setShowEmployerInformation(true);
      }
      const selectedClientId = fileData?.client?.id ?? null;
      const selectedEmployerId = fileData?.employer?.id ?? null;
      const isSwitchingSelections =
        prevSelectedClientIdRef.current !== selectedClientId ||
        prevSelectedEmployerIdRef.current !== selectedEmployerId;
      prevSelectedClientIdRef.current = selectedClientId;
      prevSelectedEmployerIdRef.current = selectedEmployerId;

      reset(
        {
          selected_client: {
            id: fileData?.client?.id,
            label: fileData?.client?.name,
          },
          selected_employer: {
            id: fileData?.employer?.id,
            label: fileData?.employer?.name,
          },
          name: fileData?.client?.name || "",
          client_id: fileData?.client?.client_id || "",
          email: fileData?.client?.email || "",
          custom_bill_rate_adjustments:
            fileData?.client?.custom_bill_rate_adjustments || "",
          employer_name: fileData?.employer?.name || "",
          address: fileData?.employer?.address || "",
          state: fileData?.employer?.state || "",
          zipcode: fileData?.employer?.zipcode || "",
          defense_counsel: fileData?.defense_counsel || "",
          defense_counsel_email: fileData?.defense_counsel_email || "",
          defense_counsel_firm_name: fileData?.defense_counsel_firm_name || "",
        },
        {
          keepDirtyValues: !isSwitchingSelections,
        },
      );
    }
  }, [fileData, reset]);

  const handleSave = useCallback(
    async (
      api: "client" | "employer" | "file",
      field: string,
      value: unknown,
    ) => {
      if (api === "client" && accessToken && clientId) {
        await patchClientById({
          id: clientId,
          data: { [field]: value },
        });
      }
      if (api === "employer" && employerId) {
        if (field === "employer_name") {
          field = "name";
        }
        await patchEmployerById({
          id: employerId,
          data: { [field]: value },
        });
      }
      if (api === "file" && accessToken && fileId) {
        await patchFileById({
          id: fileId,
          data: { [field]: value },
        });
      }
    },
    [
      patchEmployerById,
      patchClientById,
      patchFileById,

      employerId,
      clientId,
      fileId,
    ],
  );

  const handleChangeClientOnFile = useCallback(
    async (o: OptionType | null) => {
      if (accessToken && fileId) {
        await patchFileById({
          id: fileId,
          data: {
            client_id: o?.value?.toString() || null,
          },
        });
      }
    },
    [patchFileById, fileId],
  );

  const handleChangeEmployerOnFile = useCallback(
    async (o: OptionType | null) => {
      if (accessToken && fileId) {
        await patchFileById({
          id: fileId,
          data: {
            employer_id: o?.value?.toString() || null,
          },
        });
      }

      // Reset search term when employer is cleared
      if (!o) {
        setEmployerSearchTerm(null);
        setEmployerOptions([]);
      }
    },
    [patchFileById, fileId],
  );

  const handleCreateAndAssignEmployer = useCallback(
    async (employerName: string) => {
      if (!fileId) return;

      try {
        const result = await createEmployer({
          data: { name: employerName },
        }).unwrap();

        if (result.id) {
          // invalidate employers cache to refresh the list
          dispatch(api.util.invalidateTags(["Employers"]));

          // automatically assign the newly created employer to the file
          await patchFileById({
            id: fileId,
            data: { employer_id: result.id.toString() },
          });

          // reset search state and close dropdown
          setTimeout(() => {
            setEmployerSearchTerm(null);
            setEmployerOptions([]);

            // remove focus from the dropdown to close it
            if (document.activeElement) {
              (document.activeElement as HTMLElement).blur();
            }
          }, 100);
        }
      } catch (err) {
        console.error("Failed to create and assign employer:", err);
      }
    },
    [createEmployer, patchFileById, fileId, dispatch],
  );

  return (
    <Card
      heading={
        showEmployerInformation
          ? "Client + Employer Information"
          : "Client Information"
      }
    >
      <Controller
        control={control}
        name="selected_client"
        render={({ field: { name, value } }) => (
          <Select
            label="Client"
            name={name}
            errors={errors}
            options={clientOptions}
            value={value}
            placeholder="Search for a client..."
            readOnly={isReadOnly}
            isClearable={false}
            noOptionsMessage={({ inputValue }) =>
              !inputValue
                ? "Begin typing to search for clients..."
                : isClientsFetching
                  ? "Searching..."
                  : "No clients found."
            }
            onInputChange={(inputValue: string) =>
              !isReadOnly && debouncedSetClientSearchTerm(inputValue)
            }
            onChange={(o: OptionType | null) =>
              !isReadOnly && handleChangeClientOnFile(o)
            }
            required={true}
          />
        )}
      />
      <Controller
        name="name"
        control={control}
        render={({ field }) => {
          return (
            <TextInput
              {...field}
              readOnly={true}
              label="Client name"
              errors={errors}
              onBlur={() =>
                handleSave("client", field.name, getValues(field.name))
              }
            />
          );
        }}
      />
      <Controller
        name="client_id"
        control={control}
        render={({ field }) => {
          return (
            <TextInput
              {...field}
              readOnly={true}
              label="Client ID"
              errors={errors}
              onBlur={() =>
                handleSave("client", field.name, getValues(field.name))
              }
            />
          );
        }}
      />
      <Controller
        name="email"
        control={control}
        render={({ field }) => {
          return (
            <TextInput
              {...field}
              readOnly={true}
              label="Client Email"
              errors={errors}
              onBlur={() =>
                handleSave("client", field.name, getValues(field.name))
              }
            />
          );
        }}
      />
      <SectionBreak />
      <Controller
        name="defense_counsel"
        control={control}
        render={({ field }) => {
          return (
            <TextInput
              {...field}
              label="Defense Counsel Name"
              errors={errors}
              readOnly={isReadOnly}
              onBlur={() =>
                !isReadOnly &&
                handleSave("file", field.name, getValues(field.name))
              }
            />
          );
        }}
      />
      <Controller
        name="defense_counsel_email"
        control={control}
        render={({ field }) => {
          return (
            <TextInput
              {...field}
              label="Defense Counsel Email"
              errors={errors}
              readOnly={isReadOnly}
              onBlur={() =>
                !isReadOnly &&
                handleSave("file", field.name, getValues(field.name))
              }
            />
          );
        }}
      />
      <Controller
        name="defense_counsel_firm_name"
        control={control}
        render={({ field }) => {
          return (
            <TextInput
              {...field}
              label="Defense Counsel Firm"
              errors={errors}
              readOnly={isReadOnly}
              onBlur={() =>
                !isReadOnly &&
                handleSave("file", field.name, getValues(field.name))
              }
            />
          );
        }}
      />
      <SectionBreak />
      <Controller
        name="custom_bill_rate_adjustments"
        control={control}
        render={({ field }) => {
          return (
            <TextInput
              {...field}
              readOnly={true}
              label="Custom Bill Rate Adjustments"
              errors={errors}
              onBlur={() =>
                handleSave("client", field.name, getValues(field.name))
              }
            />
          );
        }}
      />
      {showEmployerInformation && (
        <>
          <SectionBreak />
          <Controller
            control={control}
            name="selected_employer"
            render={({ field: { name, value } }) => (
              <Select
                label="Employer"
                name={name}
                errors={errors}
                options={employerOptions || []}
                value={value}
                placeholder="Search for an employer..."
                readOnly={isReadOnly}
                onChange={(o: OptionType | null) =>
                  !isReadOnly && handleChangeEmployerOnFile(o)
                }
                onInputChange={(inputValue: string) =>
                  !isReadOnly && debouncedSetEmployerSearchTerm(inputValue)
                }
                required={true}
                noOptionsMessage={({ inputValue }) => {
                  if (!inputValue)
                    return "Begin typing to search for employers...";
                  if (isEmployersFetching) return "Searching...";

                  // only show "Add new item" if we have searched and found no results
                  if (
                    inputValue &&
                    !isEmployersFetching &&
                    employerSearchTerm &&
                    employerOptions.length === 0
                  ) {
                    return (
                      <div className="custom-no-options">
                        <div className="no-options-text">
                          No employers found.
                        </div>
                        <button
                          type="button"
                          className="add-new-item-btn"
                          onClick={() =>
                            handleCreateAndAssignEmployer(inputValue)
                          }
                        >
                          Add {inputValue}
                        </button>
                      </div>
                    );
                  }

                  return "No employers found.";
                }}
              />
            )}
          />
          <Controller
            name="employer_name"
            control={control}
            render={({ field }) => {
              return (
                <TextInput
                  {...field}
                  label="Employer Name"
                  readOnly={isReadOnly || !employerId}
                  errors={errors}
                  onBlur={() =>
                    !isReadOnly &&
                    handleSave("employer", field.name, getValues(field.name))
                  }
                />
              );
            }}
          />
          <Controller
            name="address"
            control={control}
            render={({ field }) => {
              return (
                <TextInput
                  {...field}
                  label="Employer Address"
                  readOnly={isReadOnly || !employerId}
                  errors={errors}
                  onBlur={() =>
                    !isReadOnly &&
                    handleSave("employer", field.name, getValues(field.name))
                  }
                />
              );
            }}
          />
          <Controller
            name="state"
            control={control}
            render={({ field: { name, value, onChange } }) => {
              return (
                <Select
                  name={name}
                  onChange={onChange}
                  label="State"
                  readOnly={isReadOnly || !employerId}
                  errors={errors}
                  options={constants?.STATES}
                  value={constants?.STATES?.find(
                    (option: OptionType) => option.value === value,
                  )}
                  onBlur={() => {
                    if (!isReadOnly) {
                      const option = getValues(name);
                      handleSave("employer", name, option.value);
                    }
                  }}
                />
              );
            }}
          />
          <Controller
            name="zipcode"
            control={control}
            render={({ field }) => {
              return (
                <MaskedInput
                  {...field}
                  mask="zipcode"
                  readOnly={isReadOnly || !employerId}
                  label="Zipcode"
                  errors={errors}
                  onBlur={() =>
                    !isReadOnly &&
                    handleSave("employer", field.name, getValues(field.name))
                  }
                />
              );
            }}
          />
        </>
      )}
    </Card>
  );
};

export default ClientAndEmployerInformation;
